<template>
  <div class="task-tile" :class="{ 'is-done': task.is_done }" @click="$emit('open', task)">
    <!-- Верх: чекбокс + срочность -->
    <div class="tile-top">
      <div class="tile-check" @click.stop>
        <Checkbox
          :modelValue="task.is_done"
          :binary="true"
          @update:modelValue="$emit('toggle', task)"
        />
      </div>
      <Tag :value="urgency.label" :severity="urgency.severity" class="tile-urgency" />
    </div>

    <!-- Название (обрезка до 2 строк) -->
    <div class="tile-title" :class="{ 'done-text': task.is_done }">{{ task.title }}</div>

    <!-- Метаданные -->
    <div class="tile-meta">
      <Tag v-if="task.label" :value="task.label" severity="contrast" icon="pi pi-tag" class="meta-label" />
      <span v-if="dueText" class="meta-due" :class="{ overdue: overdue && !task.is_done }">
        <i class="pi pi-calendar"></i> {{ dueText }}
      </span>
      <span v-if="imageCount" class="meta-photos">
        <i class="pi pi-image"></i> {{ imageCount }}
      </span>
    </div>

    <!-- Низ: исполнитель -->
    <div class="tile-footer">
      <template v-if="task.assignee">
        <span class="assignee-avatar" :style="{ background: avatarBg }">{{ initials }}</span>
        <span class="assignee-name">{{ task.assignee.username }}</span>
      </template>
      <span v-else class="assignee-none"><i class="pi pi-user"></i> Не назначен</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Checkbox from 'primevue/checkbox'
import Tag from 'primevue/tag'
import { urgencyMeta, formatDueDate, isOverdue, userInitials, avatarColor } from '../../utils/taskConstants'

const props = defineProps({
  task: { type: Object, required: true }
})

defineEmits(['toggle', 'open'])

const urgency = computed(() => urgencyMeta(props.task.urgency))
const dueText = computed(() => formatDueDate(props.task.due_date))
const overdue = computed(() => isOverdue(props.task.due_date))
const imageCount = computed(() => (props.task.images ? props.task.images.length : 0))
const initials = computed(() => userInitials(props.task.assignee?.username))
const avatarBg = computed(() => avatarColor(props.task.assignee?.username))
</script>

<style scoped>
.task-tile {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 150px;
  gap: 0.6rem;
  padding: 0.9rem 1rem;
  border: 1px solid var(--surface-200);
  border-radius: 12px;
  background: var(--surface-0);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
}

.task-tile:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.task-tile.is-done {
  opacity: 0.65;
  background: var(--surface-50);
}

.tile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tile-title {
  font-weight: 600;
  font-size: 0.98rem;
  line-height: 1.3;
  color: var(--text-color);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.done-text {
  text-decoration: line-through;
  color: var(--text-color-secondary);
}

.tile-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem 0.6rem;
  margin-top: auto;
}

.meta-due,
.meta-photos {
  font-size: 0.78rem;
  color: var(--text-color-secondary);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.meta-due.overdue {
  color: var(--red-500);
  font-weight: 600;
}

.tile-footer {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding-top: 0.55rem;
  border-top: 1px solid var(--surface-100);
}

.assignee-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.assignee-name {
  font-size: 0.82rem;
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assignee-none {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.dark-mode .task-tile {
  background: var(--surface-900);
  border-color: var(--surface-700);
}

.dark-mode .task-tile.is-done {
  background: var(--surface-800);
}

.dark-mode .tile-footer {
  border-color: var(--surface-700);
}
</style>
